/**
 * AddressFields — cascading Country → State → LGA → Ward picker plus
 * free-text street + postal code. Used by both web-customer and web-staff
 * for any form that captures a structured address.
 *
 * Data source: GET endpoints on `service-geo`. The component is given the
 * service base URL via `geoBaseUrl`; internally it caches responses in a
 * module-scoped Map so multiple `<AddressFields>` instances on the same
 * page share data without re-fetching. Dataset is essentially static, so
 * once loaded a response stays in cache for the lifetime of the page.
 *
 * Cascade behaviour:
 *   - Country first. Default selection is whichever country has sortOrder=1
 *     (Nigeria in the seeded data).
 *   - States load when a country is picked.
 *   - LGAs only render for Nigeria (state.lgaCount > 0). Other countries
 *     skip straight from State to free-text street.
 *   - Wards only render when an LGA is picked.
 *
 * Output shape (`onChange` callback):
 *   {
 *     countryId, countryName,
 *     stateId,   stateName,           // optional
 *     lgaId,     lgaName,             // optional, NG-only
 *     wardId,    wardName,            // optional, NG-only
 *     addressLine1, addressLine2,     // free-text
 *     postalCode,                     // free-text
 *   }
 *
 * Both the soft-FK ID and the denormalized name go to the consumer because
 * UPMP's no-cross-schema-FK rule means every consuming service stores both:
 * the ID for lookups and filtering, the name for display without joining.
 */
import { useEffect, useMemo, useState, type JSX } from 'react';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface AddressValue {
  countryId: number | null;
  countryName: string | null;
  stateId: number | null;
  stateName: string | null;
  lgaId: number | null;
  lgaName: string | null;
  wardId: number | null;
  wardName: string | null;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
}

export interface AddressFieldsProps {
  /** Base URL for service-geo (no trailing slash). E.g. `https://geo.upmp1.inits.dev`. */
  geoBaseUrl: string;
  value: AddressValue;
  onChange: (next: AddressValue) => void;
  /** Disable all inputs (read-only render of the captured value). */
  disabled?: boolean;
  /** Optional CSS class on the outer wrapper. */
  className?: string;
  /** Optional label for the address-line-1 field. Defaults to "Street address". */
  addressLine1Label?: string;
  /** When true, hide the postal-code input. Some forms (e.g. delivery) don't capture it. */
  hidePostalCode?: boolean;
}

export const EMPTY_ADDRESS: AddressValue = {
  countryId: null,
  countryName: null,
  stateId: null,
  stateName: null,
  lgaId: null,
  lgaName: null,
  wardId: null,
  wardName: null,
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
};

// ---------------------------------------------------------------------------
// Geo API row shapes (mirrored from apps/service-geo/src/modules/geo/geo.dto.ts)
// ---------------------------------------------------------------------------

interface CountryDto {
  id: number;
  iso2: string;
  iso3: string;
  name: string;
  sortOrder: number;
}

interface StateDto {
  id: number;
  countryId: number;
  iso: string;
  name: string;
  abbreviation: string | null;
  geoPoliticalZone: string | null;
}

interface LgaDto {
  id: number;
  stateId: number;
  name: string;
  abbreviation: string;
}

interface WardDto {
  id: number;
  localGovernmentId: number;
  name: string;
  abbreviation: string;
}

// ---------------------------------------------------------------------------
// Module-scoped cache — survives unmounts so re-mounting AddressFields
// elsewhere on the page is instant. Bounded by the dataset size (~12k rows
// total = ~5MB heap, well under any sensible limit).
// ---------------------------------------------------------------------------

interface CacheBuckets {
  countries: Promise<CountryDto[]> | CountryDto[] | null;
  states: Map<number, Promise<StateDto[]> | StateDto[]>;
  lgas: Map<number, Promise<LgaDto[]> | LgaDto[]>;
  wards: Map<number, Promise<WardDto[]> | WardDto[]>;
}

const cacheByBaseUrl = new Map<string, CacheBuckets>();

function getCache(baseUrl: string): CacheBuckets {
  let c = cacheByBaseUrl.get(baseUrl);
  if (!c) {
    c = { countries: null, states: new Map(), lgas: new Map(), wards: new Map() };
    cacheByBaseUrl.set(baseUrl, c);
  }
  return c;
}

async function fetchJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: 'application/json' } });
  if (!res.ok) {
    throw new Error(`GET ${url} failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

async function loadCountries(baseUrl: string): Promise<CountryDto[]> {
  const c = getCache(baseUrl);
  if (Array.isArray(c.countries)) return c.countries;
  if (c.countries instanceof Promise) return c.countries;
  const p = fetchJson<CountryDto[]>(`${baseUrl}/v1/geo/countries`).then((rows) => {
    c.countries = rows;
    return rows;
  });
  c.countries = p;
  return p;
}

async function loadStates(baseUrl: string, countryId: number): Promise<StateDto[]> {
  const c = getCache(baseUrl);
  const cached = c.states.get(countryId);
  if (Array.isArray(cached)) return cached;
  if (cached instanceof Promise) return cached;
  const p = fetchJson<StateDto[]>(`${baseUrl}/v1/geo/countries/${countryId}/states`).then((rows) => {
    c.states.set(countryId, rows);
    return rows;
  });
  c.states.set(countryId, p);
  return p;
}

async function loadLgas(baseUrl: string, stateId: number): Promise<LgaDto[]> {
  const c = getCache(baseUrl);
  const cached = c.lgas.get(stateId);
  if (Array.isArray(cached)) return cached;
  if (cached instanceof Promise) return cached;
  const p = fetchJson<LgaDto[]>(`${baseUrl}/v1/geo/states/${stateId}/lgas`).then((rows) => {
    c.lgas.set(stateId, rows);
    return rows;
  });
  c.lgas.set(stateId, p);
  return p;
}

async function loadWards(baseUrl: string, lgaId: number): Promise<WardDto[]> {
  const c = getCache(baseUrl);
  const cached = c.wards.get(lgaId);
  if (Array.isArray(cached)) return cached;
  if (cached instanceof Promise) return cached;
  const p = fetchJson<WardDto[]>(`${baseUrl}/v1/geo/lgas/${lgaId}/wards`).then((rows) => {
    c.wards.set(lgaId, rows);
    return rows;
  });
  c.wards.set(lgaId, p);
  return p;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AddressFields(props: AddressFieldsProps): JSX.Element {
  const {
    geoBaseUrl,
    value,
    onChange,
    disabled = false,
    className = '',
    addressLine1Label = 'Street address',
    hidePostalCode = false,
  } = props;

  const [countries, setCountries] = useState<CountryDto[]>([]);
  const [states, setStates] = useState<StateDto[]>([]);
  const [lgas, setLgas] = useState<LgaDto[]>([]);
  const [wards, setWards] = useState<WardDto[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingLgas, setLoadingLgas] = useState(false);
  const [loadingWards, setLoadingWards] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load countries once
  useEffect(() => {
    let mounted = true;
    setLoadingCountries(true);
    loadCountries(geoBaseUrl)
      .then((rows) => {
        if (!mounted) return;
        setCountries(rows);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!mounted) return;
        setError(err instanceof Error ? err.message : 'Failed to load countries');
      })
      .finally(() => mounted && setLoadingCountries(false));
    return () => {
      mounted = false;
    };
  }, [geoBaseUrl]);

  // Load states whenever country changes
  useEffect(() => {
    if (value.countryId == null) {
      setStates([]);
      return;
    }
    let mounted = true;
    setLoadingStates(true);
    loadStates(geoBaseUrl, value.countryId)
      .then((rows) => {
        if (mounted) setStates(rows);
      })
      .catch((err: unknown) => {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load states');
      })
      .finally(() => mounted && setLoadingStates(false));
    return () => {
      mounted = false;
    };
  }, [geoBaseUrl, value.countryId]);

  // Load LGAs whenever state changes (NG only — non-NG returns [])
  useEffect(() => {
    if (value.stateId == null) {
      setLgas([]);
      return;
    }
    let mounted = true;
    setLoadingLgas(true);
    loadLgas(geoBaseUrl, value.stateId)
      .then((rows) => {
        if (mounted) setLgas(rows);
      })
      .catch((err: unknown) => {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load LGAs');
      })
      .finally(() => mounted && setLoadingLgas(false));
    return () => {
      mounted = false;
    };
  }, [geoBaseUrl, value.stateId]);

  // Load wards whenever LGA changes
  useEffect(() => {
    if (value.lgaId == null) {
      setWards([]);
      return;
    }
    let mounted = true;
    setLoadingWards(true);
    loadWards(geoBaseUrl, value.lgaId)
      .then((rows) => {
        if (mounted) setWards(rows);
      })
      .catch((err: unknown) => {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load wards');
      })
      .finally(() => mounted && setLoadingWards(false));
    return () => {
      mounted = false;
    };
  }, [geoBaseUrl, value.lgaId]);

  const showLgas = lgas.length > 0;
  const showWards = wards.length > 0;
  const isNigeria = value.countryName?.toUpperCase() === 'NIGERIA';

  const handleCountry = (id: number): void => {
    const country = countries.find((c) => c.id === id);
    onChange({
      ...value,
      countryId: id,
      countryName: country?.name ?? null,
      stateId: null,
      stateName: null,
      lgaId: null,
      lgaName: null,
      wardId: null,
      wardName: null,
    });
  };

  const handleState = (id: number): void => {
    const state = states.find((s) => s.id === id);
    onChange({
      ...value,
      stateId: id,
      stateName: state?.name ?? null,
      lgaId: null,
      lgaName: null,
      wardId: null,
      wardName: null,
    });
  };

  const handleLga = (id: number): void => {
    const lga = lgas.find((l) => l.id === id);
    onChange({
      ...value,
      lgaId: id,
      lgaName: lga?.name ?? null,
      wardId: null,
      wardName: null,
    });
  };

  const handleWard = (id: number): void => {
    const ward = wards.find((w) => w.id === id);
    onChange({ ...value, wardId: id, wardName: ward?.name ?? null });
  };

  const inputCls =
    'block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm ' +
    'focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 disabled:bg-gray-50 disabled:text-gray-500';
  const labelCls = 'block text-xs font-medium text-gray-700 mb-1';

  const sortedCountries = useMemo(
    () => [...countries].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [countries],
  );

  return (
    <div className={`space-y-3 ${className}`}>
      {error ? (
        <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Country</label>
          <select
            className={inputCls}
            disabled={disabled || loadingCountries}
            value={value.countryId ?? ''}
            onChange={(e) => handleCountry(Number(e.target.value))}
          >
            <option value="" disabled>
              {loadingCountries ? 'Loading…' : 'Select country'}
            </option>
            {sortedCountries.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>{isNigeria ? 'State' : 'State / Province / Region'}</label>
          <select
            className={inputCls}
            disabled={disabled || value.countryId == null || loadingStates}
            value={value.stateId ?? ''}
            onChange={(e) => handleState(Number(e.target.value))}
          >
            <option value="" disabled>
              {value.countryId == null
                ? 'Select country first'
                : loadingStates
                  ? 'Loading…'
                  : states.length === 0
                    ? 'No subdivisions on file'
                    : 'Select state'}
            </option>
            {states.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </div>

        {showLgas ? (
          <div>
            <label className={labelCls}>Local Government Area</label>
            <select
              className={inputCls}
              disabled={disabled || value.stateId == null || loadingLgas}
              value={value.lgaId ?? ''}
              onChange={(e) => handleLga(Number(e.target.value))}
            >
              <option value="" disabled>
                {loadingLgas ? 'Loading…' : 'Select LGA'}
              </option>
              {lgas.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {showWards ? (
          <div>
            <label className={labelCls}>Ward</label>
            <select
              className={inputCls}
              disabled={disabled || value.lgaId == null || loadingWards}
              value={value.wardId ?? ''}
              onChange={(e) => handleWard(Number(e.target.value))}
            >
              <option value="" disabled>
                {loadingWards ? 'Loading…' : 'Select ward'}
              </option>
              {wards.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      <div>
        <label className={labelCls}>{addressLine1Label}</label>
        <input
          type="text"
          className={inputCls}
          disabled={disabled}
          value={value.addressLine1}
          onChange={(e) => onChange({ ...value, addressLine1: e.target.value })}
          placeholder="House number + street"
          autoComplete="address-line1"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className={labelCls}>Address line 2 (optional)</label>
          <input
            type="text"
            className={inputCls}
            disabled={disabled}
            value={value.addressLine2}
            onChange={(e) => onChange({ ...value, addressLine2: e.target.value })}
            placeholder="Apartment, suite, etc."
            autoComplete="address-line2"
          />
        </div>

        {!hidePostalCode ? (
          <div>
            <label className={labelCls}>Postal code (optional)</label>
            <input
              type="text"
              className={inputCls}
              disabled={disabled}
              value={value.postalCode}
              onChange={(e) => onChange({ ...value, postalCode: e.target.value })}
              placeholder="e.g. 100001"
              autoComplete="postal-code"
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Validation helper — re-exported so consuming forms can disable submit
// without re-implementing the cascade rules.
// ---------------------------------------------------------------------------

export interface AddressValidationResult {
  ok: boolean;
  reason?: string;
}

export function validateAddress(value: AddressValue): AddressValidationResult {
  if (value.countryId == null) return { ok: false, reason: 'Pick a country' };
  if (value.stateId == null) return { ok: false, reason: 'Pick a state' };
  if (value.addressLine1.trim().length < 3) {
    return { ok: false, reason: 'Street address must be at least 3 characters' };
  }
  return { ok: true };
}
